import React from 'react';
import { ClearFilterButton } from 'Shared/components/ClearFilterButtons';
import { MultiSelectInput } from 'Shared/components/FormInput/Variants/MultiSelect';
import { isBlank } from 'Shared/utils';
import { FilterType, FilterOption } from './types';

import styles from './filters.module.css';

type FilterOptions = {
  assetTypes: FilterOption[];
  environments: FilterOption[];
  locales: FilterOption[];
  productTypes: FilterOption[];
  productSegments: FilterOption[];
  operatingCompanies: FilterOption[];
};

type StateConfigurationsFiltersProps = {
  filters: FilterType;
  onChangeFilters: (filters: FilterType) => void;
  filterOptions: FilterOptions;
};

export const StateConfigurationsFilters: React.FC<StateConfigurationsFiltersProps> = ({
  filters,
  onChangeFilters,
  filterOptions,
}) => {
  const handleFilterChange = (
    key: keyof FilterType,
    selectedItems: FilterOption[]
  ) => {
    const newFilters = { ...filters };
    if (selectedItems.length > 0) {
      newFilters[key] = selectedItems.map(item => item.key).join(',');
    } else {
      delete newFilters[key];
    }
    onChangeFilters(newFilters);
  };

  const clearAllFilters = () => {
    onChangeFilters({});
  };

  // Update filter options with selected state based on current filters
  const getOptionsWithSelection = (
    options: FilterOption[],
    filterKey: keyof FilterType
  ): FilterOption[] => {
    const selectedValues = filters[filterKey]?.split(',') || [];
    return options.map(option => ({
      ...option,
      selected: selectedValues.includes(option.key),
    }));
  };

  return (
    <div className={styles.filtersContainer}>
      <MultiSelectInput
        label="State"
        items={getOptionsWithSelection(filterOptions.locales, 'locale')}
        onChangeSelection={item => {
          if (!item) return;
          const currentItems = getOptionsWithSelection(
            filterOptions.locales,
            'locale'
          ).filter(i => i.selected);
          const itemExists = currentItems.some(i => i.key === item?.key);
          const newItems = itemExists
            ? currentItems.filter(i => i.key !== item?.key)
            : [...currentItems, item!];
          handleFilterChange('locale', newItems);
        }}
      />

      <MultiSelectInput
        label="Product Type"
        items={getOptionsWithSelection(
          filterOptions.productTypes,
          'productType'
        )}
        onChangeSelection={item => {
          if (!item) return;
          const currentItems = getOptionsWithSelection(
            filterOptions.productTypes,
            'productType'
          ).filter(i => i.selected);
          const itemExists = currentItems.some(i => i.key === item?.key);
          const newItems = itemExists
            ? currentItems.filter(i => i.key !== item?.key)
            : [...currentItems, item!];
          handleFilterChange('productType', newItems);
        }}
      />

      <MultiSelectInput
        label="Product Segment"
        items={getOptionsWithSelection(
          filterOptions.productSegments,
          'productSegment'
        )}
        onChangeSelection={item => {
          if (!item) return;
          const currentItems = getOptionsWithSelection(
            filterOptions.productSegments,
            'productSegment'
          ).filter(i => i.selected);
          const itemExists = currentItems.some(i => i.key === item?.key);
          const newItems = itemExists
            ? currentItems.filter(i => i.key !== item?.key)
            : [...currentItems, item!];
          handleFilterChange('productSegment', newItems);
        }}
      />

      <MultiSelectInput
        label="Operating Company"
        items={getOptionsWithSelection(
          filterOptions.operatingCompanies,
          'operatingCompany'
        )}
        onChangeSelection={item => {
          if (!item) return;
          const currentItems = getOptionsWithSelection(
            filterOptions.operatingCompanies,
            'operatingCompany'
          ).filter(i => i.selected);
          const itemExists = currentItems.some(i => i.key === item?.key);
          const newItems = itemExists
            ? currentItems.filter(i => i.key !== item?.key)
            : [...currentItems, item!];
          handleFilterChange('operatingCompany', newItems);
        }}
      />

      <MultiSelectInput
        label="Asset Type"
        items={getOptionsWithSelection(filterOptions.assetTypes, 'assetType')}
        onChangeSelection={item => {
          if (!item) return;
          const currentItems = getOptionsWithSelection(
            filterOptions.assetTypes,
            'assetType'
          ).filter(i => i.selected);
          const itemExists = currentItems.some(i => i.key === item?.key);
          const newItems = itemExists
            ? currentItems.filter(i => i.key !== item?.key)
            : [...currentItems, item!];
          handleFilterChange('assetType', newItems);
        }}
      />

      <MultiSelectInput
        label="Environment"
        items={getOptionsWithSelection(
          filterOptions.environments,
          'environment'
        )}
        onChangeSelection={item => {
          if (!item) return;
          const currentItems = getOptionsWithSelection(
            filterOptions.environments,
            'environment'
          ).filter(i => i.selected);
          const itemExists = currentItems.some(i => i.key === item?.key);
          const newItems = itemExists
            ? currentItems.filter(i => i.key !== item?.key)
            : [...currentItems, item!];
          handleFilterChange('environment', newItems);
        }}
      />

      {!isBlank(filters) && (
        <ClearFilterButton
          onClick={clearAllFilters}
          disabled={false}
          className={styles.clearButton}
        >
          Clear Filters
        </ClearFilterButton>
      )}
    </div>
  );
};
